import { useMemo, useCallback, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  CodeBlock,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@altimateai/lego";
import { CopyButton } from "../../components/copyButton";
import { MCPIntegrationType } from "../mcp/constants";
import useIntegrations from "./useIntegrations";
import ViewConnections from "../mcp/ViewConnections";
import { getTenant } from "@/helpers/tenant";
import { API_URL } from "@/config";
import { useAppState } from "@/modules/app/useAppContext";
import { useConnections } from "@/hooks/useConnections";
import { Teammate } from "../mcp/mcpSlice";

type TransportType = "http" | "sse";

const TRANSPORT_CONFIG = {
  http: {
    type: "http",
    url: "https://mcpserver.getaltimate.com/mcp",
  },
  sse: {
    type: "sse",
    url: "https://mcpserver.getaltimate.com/sse",
  },
} as const;

interface RemoteServerSetupInstructionsProps {
  teammate: Teammate;
}

const RemoteServerSetupInstructions = ({ teammate }: RemoteServerSetupInstructionsProps) => {
  const { loggedinUser: user } = useAppState();

  const { allIntegrations, isCustomIntegrationsLoading, isIntegrationsFromServerLoading } =
    useIntegrations();

  // Use connections hook with teammate integrations
  const {
    selectedConnections,
    handleConnectionSelect,
    isLoading: isConnectionsLoading,
  } = useConnections({
    integrations: teammate?.integrations?.map(int => int.id) || [],
  });

  const isLoading =
    isCustomIntegrationsLoading || isIntegrationsFromServerLoading || isConnectionsLoading;

  // Get integration data for datamate's integrations
  const datamateIntegrationsWithData = useMemo(
    () =>
      teammate?.integrations
        ?.map(datamateIntegration => {
          const integrationData = allIntegrations.find(
            integration => integration.id === datamateIntegration.id
          );
          return integrationData;
        })
        .filter(Boolean) || [],
    [teammate?.integrations, allIntegrations]
  );

  const [transportType, setTransportType] = useState<TransportType>("http");

  // Generate MCP server config for a given transport type
  const generateMCPConfig = useCallback(
    (transport: TransportType) => {
      const { type, url } = TRANSPORT_CONFIG[transport];

      // Return empty config if data not loaded yet
      if (!teammate || !user) {
        return {
          mcpServers: {
            datamate: {
              type,
              url,
              headers: {
                Authorization: "Bearer YOUR_TOKEN",
                "x-datamate-id": "YOUR_DATAMATE_ID",
                "x-tenant": "YOUR_TENANT",
                "x-altimate-url": "YOUR_API_URL",
              },
            },
          },
        };
      }

      const connectionHeaders: Record<string, string> = {};

      // Add connection headers for each selected connection
      Object.entries(selectedConnections).forEach(([integrationId, connectionId]) => {
        const integration = datamateIntegrationsWithData.find(int => int?.id === integrationId);
        if (integration?.name) {
          connectionHeaders[`x-connection-${integration.name.toLowerCase().replace(/\s+/g, "-")}`] =
            connectionId;
        }
      });

      return {
        mcpServers: {
          datamate: {
            type,
            url,
            headers: {
              Authorization: `Bearer ${user?.auth_token}`,
              "x-datamate-id": teammate.id?.toString() || "",
              "x-tenant": getTenant(),
              "x-altimate-url": API_URL,
              ...connectionHeaders,
            },
          },
        },
      };
    },
    [teammate, user, selectedConnections, datamateIntegrationsWithData]
  );

  const configJsonString = useMemo(() => {
    return JSON.stringify(generateMCPConfig(transportType), null, 2);
  }, [generateMCPConfig, transportType]);

  return (
    <div className="al-max-w-3xl al-mx-auto">
      <p>Follow these steps to set up your remote MCP server connection:</p>

      <div className="al-space-y-6">
        {/* Connection Selection */}
        <Card>
          <CardHeader className="!al-p-3 !al-pb-0">
            <Typography variant="section-heading">1. Select Connections</Typography>
          </CardHeader>
          <CardContent className="!al-p-3">
            <p className="al-text-gray-700 al-mb-4">
              Choose the connections you want to use for each integration. The first connection per
              integration is pre-selected.
            </p>
            <div className="al-space-y-4">
              {isLoading ? (
                <p className="al-text-sm al-text-muted-foreground">Loading integrations...</p>
              ) : (
                <>
                  {datamateIntegrationsWithData.length === 0 ? (
                    <p className="al-text-sm al-text-muted-foreground">
                      No integrations configured
                    </p>
                  ) : (
                    datamateIntegrationsWithData.map(integration => {
                      if (integration?.type === MCPIntegrationType.EXTENSION) return null;

                      return (
                        <div key={integration?.id} className="al-border al-rounded-lg al-p-4">
                          <Typography variant="body" className="al-mb-2">
                            {integration?.name}
                          </Typography>
                          <ViewConnections
                            integration={integration!}
                            onSelect={handleConnectionSelect}
                            selectedConnectionId={selectedConnections[integration?.id || ""]}
                          />
                        </div>
                      );
                    })
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Configuration Display */}
        <Card>
          <CardHeader className="!al-p-3 !al-pb-0">
            <Typography variant="section-heading">2. Copy Configuration</Typography>
          </CardHeader>
          <CardContent className="!al-p-3">
            <p className="al-text-gray-700 al-mb-4">
              Select the transport type and copy the configuration for your remote MCP server setup:
            </p>
            <Tabs
              value={transportType}
              onValueChange={value => setTransportType(value as TransportType)}
            >
              <TabsList>
                <TabsTrigger value="http">Streamable HTTP</TabsTrigger>
                <TabsTrigger value="sse">SSE</TabsTrigger>
              </TabsList>
              <TabsContent value="http">
                <p className="al-text-sm al-text-muted-foreground al-mb-3">
                  Recommended for most MCP clients. For Cursor, use{" "}
                  <code className="al-text-xs al-bg-muted al-px-1 al-py-0.5 al-rounded">
                    streamable-http
                  </code>{" "}
                  as the type.
                </p>
                <div className="al-space-y-4">
                  <CodeBlock
                    code={configJsonString}
                    language="json"
                    theme="light"
                    editorTheme="tomorrow"
                  />
                  <CopyButton value={configJsonString} label="Copy Configuration" />
                </div>
              </TabsContent>
              <TabsContent value="sse">
                <p className="al-text-sm al-text-muted-foreground al-mb-3">
                  Use SSE (Server-Sent Events) for clients that do not support streamable HTTP.
                </p>
                <div className="al-space-y-4">
                  <CodeBlock
                    code={configJsonString}
                    language="json"
                    theme="light"
                    editorTheme="tomorrow"
                  />
                  <CopyButton value={configJsonString} label="Copy Configuration" />
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RemoteServerSetupInstructions;
